import { MapMarker } from "../components/map/map.types";

/**
 * Fits the map bounds to display all provided markers and updates the zoom level
 * @param mapInstance - Google Maps instance
 * @param markers - Array of markers to fit within bounds
 * @param onMapZoomChange - Callback to update zoom state
 * @param enforceMinZoom - If true, ensures a minimum zoom level of 13 (useful for region/route selection)
 */
export const fitMapToMarkers = (
  mapInstance: google.maps.Map,
  markers: MapMarker[],
  onMapZoomChange: (zoom: number) => void,
  enforceMinZoom = false,
): void => {
  if (!mapInstance || !markers || markers.length === 0) {
    return;
  }

  const bounds = new google.maps.LatLngBounds();

  // Add all markers to bounds
  markers.forEach((marker) => {
    if (marker.latitude && marker.longitude) {
      bounds.extend(new google.maps.LatLng(marker.latitude, marker.longitude));
    }
  });

  // Fit the map to the bounds of the markers.
  // NOTE: Do not reset the map to zoom 0 / {0,0} beforehand. Doing so makes the
  // map jump to the world view, which forces the marker clusterer to collapse
  // every marker into world-level clusters and then re-expand them, producing a
  // visible "disappear/reappear" flash. fitBounds computes the correct viewport
  // directly from the bounds, so the reset is unnecessary and harmful.
  mapInstance.fitBounds(bounds);
  mapInstance.setCenter(bounds.getCenter());

  // Update zoom state
  const newZoom = mapInstance.getZoom();
  if (newZoom !== null && newZoom !== undefined) {
    let adjustedZoom = newZoom;

    // Only enforce minimum zoom when explicitly requested (e.g., region/route selection)
    // This prevents being too zoomed out when viewing a selected region
    if (enforceMinZoom) {
      adjustedZoom = Math.max(adjustedZoom, 13);
    }

    mapInstance.setZoom(adjustedZoom);
    onMapZoomChange(adjustedZoom);
  }
};
